/**
 * Utility functions for formatting data for human-readable display
 */

/**
 * Format bytes into human-readable size with appropriate units
 * 
 * Converts raw byte counts into readable format using binary units (1024-based).
 * Automatically selects the most appropriate unit (B, KB, MB, GB) and formats
 * to one decimal place.
 * 
 * @param bytes - Number of bytes to format
 * @returns Formatted string with size and unit (e.g., "1.5 MB", "512.0 B")
 * 
 * @example
 * ```typescript
 * formatBytes(1024)       // "1.0 KB"
 * formatBytes(1048576)    // "1.0 MB"
 * formatBytes(1536)       // "1.5 KB"
 * formatBytes(0)          // "0.0 B"
 * formatBytes(500)        // "500.0 B"
 * ```
 */
export function formatBytes(bytes: number): string {
  // Handle negative numbers and zero
  if (bytes <= 0) {
    return '0.0 B';
  }

  // Round very small numbers to zero
  if (bytes < 1) {
    return '0.0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  // Convert to appropriate unit using binary (1024) conversion
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  // Format to one decimal place with appropriate unit
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

/**
 * Legacy alias for formatBytes to maintain backward compatibility
 * @deprecated Use formatBytes instead
 */
export const formatStorageSize = formatBytes;

/**
 * Format an amount as Indonesian Rupiah with no decimal places.
 *
 * The single canonical IDR currency formatter for the app. IDR has no minor
 * unit, so fraction digits are pinned to 0. Output looks like `Rp 1.500.000`.
 *
 * @param amount - IDR amount to format
 * @returns Formatted currency string (e.g. "Rp 1.500.000")
 *
 * @example
 * ```typescript
 * formatPriceIDR(1500000)  // "Rp 1.500.000"
 * formatPriceIDR(0)        // "Rp 0"
 * ```
 */
/** Grouped count/quantity formatting with Indonesian separators (1.234.567). */
export function formatCount(n: number): string {
  return n.toLocaleString('id-ID');
}

export function formatPriceIDR(amount: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}